import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { Line } from 'react-chartjs-2';
import moment from 'moment';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import * as adminService from '../../services/admin';

const NotificationManagement = () => {
    const [activeTab, setActiveTab] = useState('overview');
    const [templates, setTemplates] = useState([]);
    const [logs, setLogs] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [statistics, setStatistics] = useState({
        statistics: {},
        trend: []
    });
    const [selectedBranch, setSelectedBranch] = useState(localStorage.getItem('branch_id') || '1');
    const [loading, setLoading] = useState(false);

    // Template modal state
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [templateForm, setTemplateForm] = useState({
        template_name: '',
        template_code: '',
        notification_type: 'email',
        subject: '',
        email_body: '',
        sms_body: '',
        trigger_event: 'custom',
        placeholders: [],
        is_active: true
    });
    const [editingTemplateId, setEditingTemplateId] = useState(null);

    // Send notification modal state
    const [showSendModal, setShowSendModal] = useState(false);
    const [sendForm, setSendForm] = useState({
        template_id: '',
        notification_type: 'email',
        recipient: '',
        data: {}
    });

    // Schedule modal state
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [scheduleForm, setScheduleForm] = useState({
        template_id: '',
        schedule_name: '',
        frequency: 'daily',
        scheduled_date: '',
        scheduled_time: '09:00',
        day_of_week: 'Monday',
        day_of_month: 1,
        recipient_criteria: {},
        is_active: true
    });
    const [editingScheduleId, setEditingScheduleId] = useState(null);

    useEffect(() => {
        if (activeTab === 'templates') {
            fetchTemplates();
        } else if (activeTab === 'logs') {
            fetchLogs();
        } else if (activeTab === 'schedules') {
            fetchSchedules();
        } else if (activeTab === 'overview') {
            fetchStatistics();
        }
    }, [activeTab, selectedBranch]);

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            const response = await adminService.getTemplates({});
            if (response.status === 'success') {
                setTemplates(response.data);
            }
        } catch (error) {
            toast.error('Failed to fetch templates');
        } finally {
            setLoading(false);
        }
    };

    const fetchLogs = async () => {
        try {
            setLoading(true);
            const response = await adminService.getNotificationLogs({ branch_id: selectedBranch });
            if (response.status === 'success') {
                setLogs(response.data);
            }
        } catch (error) {
            toast.error('Failed to fetch logs');
        } finally {
            setLoading(false);
        }
    };

    const fetchSchedules = async () => {
        try {
            setLoading(true);
            const response = await adminService.getScheduledNotifications({ branch_id: selectedBranch });
            if (response.status === 'success') {
                setSchedules(response.data);
            }
        } catch (error) {
            toast.error('Failed to fetch schedules');
        } finally {
            setLoading(false);
        }
    };

    const fetchStatistics = async () => {
        try {
            setLoading(true);
            const response = await adminService.getNotificationStatistics({
                branch_id: selectedBranch,
                days: 30
            });
            if (response.status === 'success') {
                setStatistics(response.data);
            }
        } catch (error) {
            toast.error('Failed to fetch statistics');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveTemplate = async () => {
        try {
            if (editingTemplateId) {
                await adminService.updateTemplate(editingTemplateId, templateForm);
                toast.success('Template updated successfully');
            } else {
                await adminService.createTemplate(templateForm);
                toast.success('Template created successfully');
            }
            setShowTemplateModal(false);
            fetchTemplates();
            resetTemplateForm();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save template');
        }
    };

    const handleEditTemplate = (template) => {
        setTemplateForm(template);
        setEditingTemplateId(template.id);
        setShowTemplateModal(true);
    };

    const handleDeleteTemplate = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'This will delete the template permanently',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await adminService.deleteTemplate(id);
                toast.success('Template deleted successfully');
                fetchTemplates();
            } catch (error) {
                toast.error('Failed to delete template');
            }
        }
    };

    const handleSendNotification = async () => {
        try {
            await adminService.sendNotification({
                ...sendForm,
                branch_id: selectedBranch
            });
            toast.success('Notification queued successfully');
            setShowSendModal(false);
            resetSendForm();
            fetchLogs();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to send notification');
        }
    };

    const handleRetryNotification = async (id) => {
        try {
            await adminService.retryNotification(id);
            toast.success('Notification queued for retry');
            fetchLogs();
        } catch (error) {
            toast.error('Failed to retry notification');
        }
    };

    const handleSaveSchedule = async () => {
        try {
            const data = {
                ...scheduleForm,
                branch_id: selectedBranch
            };

            if (editingScheduleId) {
                await adminService.updateScheduledNotification(editingScheduleId, data);
                toast.success('Schedule updated successfully');
            } else {
                await adminService.createScheduledNotification(data);
                toast.success('Schedule created successfully');
            }
            setShowScheduleModal(false);
            fetchSchedules();
            resetScheduleForm();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save schedule');
        }
    };

    const handleEditSchedule = (schedule) => {
        setScheduleForm(schedule);
        setEditingScheduleId(schedule.id);
        setShowScheduleModal(true);
    };

    const handleDeleteSchedule = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'This will delete the schedule permanently',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await adminService.deleteScheduledNotification(id);
                toast.success('Schedule deleted successfully');
                fetchSchedules();
            } catch (error) {
                toast.error('Failed to delete schedule');
            }
        }
    };

    const resetTemplateForm = () => {
        setTemplateForm({
            template_name: '',
            template_code: '',
            notification_type: 'email',
            subject: '',
            email_body: '',
            sms_body: '',
            trigger_event: 'custom',
            placeholders: [],
            is_active: true
        });
        setEditingTemplateId(null);
    };

    const resetSendForm = () => {
        setSendForm({
            template_id: '',
            notification_type: 'email',
            recipient: '',
            data: {}
        });
    };

    const resetScheduleForm = () => {
        setScheduleForm({
            template_id: '',
            schedule_name: '',
            frequency: 'daily',
            scheduled_date: '',
            scheduled_time: '09:00',
            day_of_week: 'Monday',
            day_of_month: 1,
            recipient_criteria: {},
            is_active: true
        });
        setEditingScheduleId(null);
    };

    const templateColumnDefs = [
        { field: 'id', headerName: 'ID', width: 80 },
        { field: 'template_name', headerName: 'Template Name', width: 200 },
        { field: 'template_code', headerName: 'Code', width: 150 },
        { field: 'notification_type', headerName: 'Type', width: 120 },
        { field: 'trigger_event', headerName: 'Trigger Event', width: 150 },
        {
            field: 'is_active',
            headerName: 'Status',
            width: 100,
            cellRenderer: (params) => (
                <span className={`badge ${params.value ? 'bg-success' : 'bg-danger'}`}>
                    {params.value ? 'Active' : 'Inactive'}
                </span>
            )
        },
        {
            headerName: 'Actions',
            width: 180,
            cellRenderer: (params) => (
                <div>
                    <button
                        className="btn btn-sm btn-primary me-2"
                        onClick={() => handleEditTemplate(params.data)}
                    >
                        Edit
                    </button>
                    <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteTemplate(params.data.id)}
                    >
                        Delete
                    </button>
                </div>
            )
        }
    ];

    const logColumnDefs = [
        { field: 'id', headerName: 'ID', width: 80 },
        {
            field: 'created_at',
            headerName: 'Date',
            width: 150,
            valueFormatter: (params) => moment(params.value).format('YYYY-MM-DD HH:mm')
        },
        { field: 'notification_type', headerName: 'Type', width: 100 },
        { field: 'recipient', headerName: 'Recipient', width: 200 },
        { field: 'subject', headerName: 'Subject', width: 200 },
        {
            field: 'status',
            headerName: 'Status',
            width: 120,
            cellRenderer: (params) => {
                const statusClass = {
                    'sent': 'bg-success',
                    'failed': 'bg-danger',
                    'pending': 'bg-warning',
                    'queued': 'bg-info'
                };
                return (
                    <span className={`badge ${statusClass[params.value]}`}>
                        {params.value}
                    </span>
                );
            }
        },
        {
            headerName: 'Actions',
            width: 120,
            cellRenderer: (params) => (
                params.data.status === 'failed' ? (
                    <button
                        className="btn btn-sm btn-warning"
                        onClick={() => handleRetryNotification(params.data.id)}
                    >
                        Retry
                    </button>
                ) : null
            )
        }
    ];

    const scheduleColumnDefs = [
        { field: 'id', headerName: 'ID', width: 80 },
        { field: 'schedule_name', headerName: 'Schedule Name', width: 200 },
        {
            field: 'template.template_name',
            headerName: 'Template',
            width: 200,
            valueGetter: (params) => params.data.template?.template_name || ''
        },
        { field: 'frequency', headerName: 'Frequency', width: 120 },
        { field: 'scheduled_time', headerName: 'Time', width: 100 },
        {
            field: 'next_run_at',
            headerName: 'Next Run',
            width: 150,
            valueFormatter: (params) => params.value ? moment(params.value).format('YYYY-MM-DD HH:mm') : 'N/A'
        },
        {
            field: 'is_active',
            headerName: 'Status',
            width: 100,
            cellRenderer: (params) => (
                <span className={`badge ${params.value ? 'bg-success' : 'bg-danger'}`}>
                    {params.value ? 'Active' : 'Inactive'}
                </span>
            )
        },
        {
            headerName: 'Actions',
            width: 180,
            cellRenderer: (params) => (
                <div>
                    <button
                        className="btn btn-sm btn-primary me-2"
                        onClick={() => handleEditSchedule(params.data)}
                    >
                        Edit
                    </button>
                    <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteSchedule(params.data.id)}
                    >
                        Delete
                    </button>
                </div>
            )
        }
    ];

    const renderOverview = () => {
        const trendData = {
            labels: statistics.trend.map(item => moment(item.date).format('MMM DD')),
            datasets: [
                {
                    label: 'Sent',
                    data: statistics.trend.map(item => item.sent),
                    borderColor: '#28a745',
                    backgroundColor: 'rgba(40, 167, 69, 0.1)',
                    tension: 0.4
                },
                {
                    label: 'Failed',
                    data: statistics.trend.map(item => item.failed),
                    borderColor: '#dc3545',
                    backgroundColor: 'rgba(220, 53, 69, 0.1)',
                    tension: 0.4
                }
            ]
        };

        return (
            <div>
                <div className="row mb-4">
                    <div className="col-md-3">
                        <div className="card bg-success text-white">
                            <div className="card-body">
                                <h5>Total Sent</h5>
                                <h2>{statistics.statistics.total_sent || 0}</h2>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card bg-danger text-white">
                            <div className="card-body">
                                <h5>Total Failed</h5>
                                <h2>{statistics.statistics.total_failed || 0}</h2>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card bg-warning text-white">
                            <div className="card-body">
                                <h5>Pending</h5>
                                <h2>{statistics.statistics.total_pending || 0}</h2>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card bg-info text-white">
                            <div className="card-body">
                                <h5>Emails Sent</h5>
                                <h2>{statistics.statistics.emails_sent || 0}</h2>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-header">
                        <h5>Notification Trend (Last 7 Days)</h5>
                    </div>
                    <div className="card-body">
                        <Line data={trendData} options={{ responsive: true, maintainAspectRatio: false }} height={300} />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <h4>Notification Management</h4>
                        </div>
                        <div className="card-body">
                            <ul className="nav nav-tabs mb-3">
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('overview')}
                                    >
                                        Overview
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'templates' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('templates')}
                                    >
                                        Templates
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'send' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('send')}
                                    >
                                        Send Notification
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'logs' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('logs')}
                                    >
                                        Logs
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'schedules' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('schedules')}
                                    >
                                        Schedules
                                    </button>
                                </li>
                            </ul>

                            {activeTab === 'overview' && renderOverview()}

                            {activeTab === 'templates' && (
                                <div>
                                    <button
                                        className="btn btn-primary mb-3"
                                        onClick={() => {
                                            resetTemplateForm();
                                            setShowTemplateModal(true);
                                        }}
                                    >
                                        Create Template
                                    </button>
                                    <div className="ag-theme-alpine" style={{ height: 500, width: '100%' }}>
                                        <AgGridReact
                                            rowData={templates}
                                            columnDefs={templateColumnDefs}
                                            pagination={true}
                                            paginationPageSize={20}
                                        />
                                    </div>
                                </div>
                            )}

                            {activeTab === 'send' && (
                                <div className="card">
                                    <div className="card-header">
                                        <h5>Send Notification</h5>
                                    </div>
                                    <div className="card-body">
                                        <div className="mb-3">
                                            <label className="form-label">Template</label>
                                            <select
                                                className="form-control"
                                                value={sendForm.template_id}
                                                onChange={(e) => setSendForm({...sendForm, template_id: e.target.value})}
                                            >
                                                <option value="">Select Template</option>
                                                {templates.filter(t => t.is_active).map(template => (
                                                    <option key={template.id} value={template.id}>
                                                        {template.template_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Notification Type</label>
                                            <select
                                                className="form-control"
                                                value={sendForm.notification_type}
                                                onChange={(e) => setSendForm({...sendForm, notification_type: e.target.value})}
                                            >
                                                <option value="email">Email</option>
                                                <option value="sms">SMS</option>
                                            </select>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">
                                                {sendForm.notification_type === 'email' ? 'Email Address' : 'Phone Number'}
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={sendForm.recipient}
                                                onChange={(e) => setSendForm({...sendForm, recipient: e.target.value})}
                                            />
                                        </div>
                                        <button
                                            className="btn btn-primary"
                                            onClick={handleSendNotification}
                                        >
                                            Send Notification
                                        </button>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'logs' && (
                                <div className="ag-theme-alpine" style={{ height: 500, width: '100%' }}>
                                    <AgGridReact
                                        rowData={logs}
                                        columnDefs={logColumnDefs}
                                        pagination={true}
                                        paginationPageSize={20}
                                    />
                                </div>
                            )}

                            {activeTab === 'schedules' && (
                                <div>
                                    <button
                                        className="btn btn-primary mb-3"
                                        onClick={() => {
                                            resetScheduleForm();
                                            setShowScheduleModal(true);
                                        }}
                                    >
                                        Create Schedule
                                    </button>
                                    <div className="ag-theme-alpine" style={{ height: 500, width: '100%' }}>
                                        <AgGridReact
                                            rowData={schedules}
                                            columnDefs={scheduleColumnDefs}
                                            pagination={true}
                                            paginationPageSize={20}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Template Modal */}
            {showTemplateModal && (
                <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editingTemplateId ? 'Edit Template' : 'Create Template'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowTemplateModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Template Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={templateForm.template_name}
                                        onChange={(e) => setTemplateForm({...templateForm, template_name: e.target.value})}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Template Code</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={templateForm.template_code}
                                        onChange={(e) => setTemplateForm({...templateForm, template_code: e.target.value})}
                                        disabled={editingTemplateId}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Notification Type</label>
                                    <select
                                        className="form-control"
                                        value={templateForm.notification_type}
                                        onChange={(e) => setTemplateForm({...templateForm, notification_type: e.target.value})}
                                    >
                                        <option value="email">Email</option>
                                        <option value="sms">SMS</option>
                                        <option value="both">Both</option>
                                    </select>
                                </div>
                                {(templateForm.notification_type === 'email' || templateForm.notification_type === 'both') && (
                                    <>
                                        <div className="mb-3">
                                            <label className="form-label">Email Subject</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={templateForm.subject}
                                                onChange={(e) => setTemplateForm({...templateForm, subject: e.target.value})}
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Email Body</label>
                                            <textarea
                                                className="form-control"
                                                rows="5"
                                                value={templateForm.email_body}
                                                onChange={(e) => setTemplateForm({...templateForm, email_body: e.target.value})}
                                            ></textarea>
                                        </div>
                                    </>
                                )}
                                {(templateForm.notification_type === 'sms' || templateForm.notification_type === 'both') && (
                                    <div className="mb-3">
                                        <label className="form-label">SMS Body</label>
                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            value={templateForm.sms_body}
                                            onChange={(e) => setTemplateForm({...templateForm, sms_body: e.target.value})}
                                        ></textarea>
                                    </div>
                                )}
                                <div className="mb-3">
                                    <label className="form-label">Trigger Event</label>
                                    <select
                                        className="form-control"
                                        value={templateForm.trigger_event}
                                        onChange={(e) => setTemplateForm({...templateForm, trigger_event: e.target.value})}
                                    >
                                        <option value="loan_approved">Loan Approved</option>
                                        <option value="loan_disbursed">Loan Disbursed</option>
                                        <option value="collection_due">Collection Due</option>
                                        <option value="collection_received">Collection Received</option>
                                        <option value="payment_overdue">Payment Overdue</option>
                                        <option value="member_registered">Member Registered</option>
                                        <option value="password_reset">Password Reset</option>
                                        <option value="custom">Custom</option>
                                    </select>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowTemplateModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSaveTemplate}
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Schedule Modal */}
            {showScheduleModal && (
                <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editingScheduleId ? 'Edit Schedule' : 'Create Schedule'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowScheduleModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Schedule Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={scheduleForm.schedule_name}
                                        onChange={(e) => setScheduleForm({...scheduleForm, schedule_name: e.target.value})}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Template</label>
                                    <select
                                        className="form-control"
                                        value={scheduleForm.template_id}
                                        onChange={(e) => setScheduleForm({...scheduleForm, template_id: e.target.value})}
                                    >
                                        <option value="">Select Template</option>
                                        {templates.filter(t => t.is_active).map(template => (
                                            <option key={template.id} value={template.id}>
                                                {template.template_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Frequency</label>
                                    <select
                                        className="form-control"
                                        value={scheduleForm.frequency}
                                        onChange={(e) => setScheduleForm({...scheduleForm, frequency: e.target.value})}
                                    >
                                        <option value="once">Once</option>
                                        <option value="daily">Daily</option>
                                        <option value="weekly">Weekly</option>
                                        <option value="monthly">Monthly</option>
                                    </select>
                                </div>
                                {scheduleForm.frequency === 'once' && (
                                    <div className="mb-3">
                                        <label className="form-label">Scheduled Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={scheduleForm.scheduled_date}
                                            onChange={(e) => setScheduleForm({...scheduleForm, scheduled_date: e.target.value})}
                                        />
                                    </div>
                                )}
                                <div className="mb-3">
                                    <label className="form-label">Scheduled Time</label>
                                    <input
                                        type="time"
                                        className="form-control"
                                        value={scheduleForm.scheduled_time}
                                        onChange={(e) => setScheduleForm({...scheduleForm, scheduled_time: e.target.value})}
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowScheduleModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSaveSchedule}
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationManagement;
